import { useCallback, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Series } from '~/domain/dicom/series';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { getRegistrationStore } from '~/domain/ServiceContainer';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import {
    imageListVersionState,
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';
import useSelectedPatient from './patient/useSelectedPatient';
import useViewportEnvironment from './useViewportEnvironment';
import useViewportSecondaryEnvironment from './useViewportSecondaryEnvironment';

export default function useDeleteDataset() {
    const patient = useSelectedPatient();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const currentDose = useRecoilValue(currentDoseState);
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const {
        removePrimaryImage,
        removeEnvironmentSecondaryImage,
        unloadDose,
        unloadStructureSet,
    } = useViewportEnvironment();
    const { removeEnvironmentSecondaryImageSideBySide } =
        useViewportSecondaryEnvironment();
    const setImageListVersion = useSetRecoilState(imageListVersionState);

    const getAffectedStructureSets = useCallback(
        (image: Image3D): StructureSet[] => {
            if (!patient) {
                return [];
            }
            return patient.getAffectedStructureSets(image);
        },
        [patient]
    );

    const deleteImage = useCallback(
        (image: Image3D) => {
            if (!patient) {
                return;
            }
            if (secondaryImage?.dicomUid === image.dicomUid) {
                removeEnvironmentSecondaryImage();
                removeEnvironmentSecondaryImageSideBySide();
            }
            if (primaryImage?.dicomUid === image.dicomUid) {
                removePrimaryImage();
            }
            patient.removeImage(image);
            dicomProcessor.removeSeriesFromPatient(
                patient.patientId,
                image.seriesUid
            );
            setImageListVersion((v) => v + 1);
        },
        [
            patient,
            primaryImage,
            secondaryImage,
            dicomProcessor,
            removePrimaryImage,
            removeEnvironmentSecondaryImage,
            removeEnvironmentSecondaryImageSideBySide,
            setImageListVersion,
        ]
    );

    const deleteRawSeries = useCallback(
        (series: Series) => {
            if (!patient) {
                return;
            }
            dicomProcessor.removeSeriesFromPatient(
                patient.patientId,
                series.seriesInstanceUID
            );
            setImageListVersion((v) => v + 1);
        },
        [patient, dicomProcessor, setImageListVersion]
    );

    const deleteDose = useCallback(
        (dose: DoseMatrix) => {
            if (!patient) {
                return;
            }
            if (currentDose?.dicomUid === dose.dicomUid) {
                unloadDose();
            }
            patient.removeDose(dose);
            dicomProcessor.removeSopObjectFromPatient(
                patient.patientId,
                dose.sopInstanceUid
            );
        },
        [patient, currentDose, dicomProcessor, unloadDose]
    );

    const deleteStructureSet = useCallback(
        (structureSet: StructureSet) => {
            if (!patient) {
                return;
            }
            const sopUid = structureSet.dicomStructureSet.sopInstanceUid;
            if (currentStructureSetId === sopUid) {
                unloadStructureSet();
            }
            patient.removeStructureSet(structureSet);
            dicomProcessor.removeSopObjectFromPatient(
                patient.patientId,
                sopUid
            );
        },
        [patient, currentStructureSetId, dicomProcessor, unloadStructureSet]
    );

    const deleteRegistration = useCallback(
        (registration: RigidRegistration) => {
            if (!patient) {
                return;
            }
            const store = getRegistrationStore();
            store.deleteRegistration(registration.dicomUid);
            dicomProcessor.removeSopObjectFromPatient(
                patient.patientId,
                registration.sopInstanceUid
            );
        },
        [patient, dicomProcessor]
    );

    return useMemo(
        () => ({
            deleteImage,
            deleteRawSeries,
            deleteDose,
            deleteStructureSet,
            deleteRegistration,
            getAffectedStructureSets,
        }),
        [
            deleteImage,
            deleteRawSeries,
            deleteDose,
            deleteStructureSet,
            deleteRegistration,
            getAffectedStructureSets,
        ]
    );
}
